/**
 * oidc.ts — OAuth 2.0 Authorization Code flow with PKCE against any
 * OIDC-discovery-compliant identity provider.
 *
 * The SPA is registered as a public client; PKCE is mandatory. We hand-roll
 * the flow here rather than pulling in a runtime OIDC client library because
 * (a) most adapters are opinionated about iframe-based silent refresh which
 * doesn't work cleanly with Vite dev, and (b) ~100 lines of standards-compliant
 * OIDC is easier to audit than a 200KB runtime dep.
 *
 * Nothing in this module is provider-specific: every endpoint is read from
 * the issuer's `/.well-known/openid-configuration` discovery document. That
 * is a hard requirement — self-hosters point the SPA at whatever
 * OIDC-compliant IdP they run, not just the hosted default (Zitadel).
 *
 * Configuration is read from Vite env vars at build time:
 *
 *   VITE_OIDC_ISSUER     e.g. https://id.internal.nrmitchi.com
 *   VITE_OIDC_CLIENT_ID  e.g. the SPA's registered public-client ID
 *
 * See `.env.example` for the full self-host configuration surface.
 */

import { setAccessToken } from "./auth";

export interface OidcConfig {
  issuer: string;
  clientId: string;
}

/**
 * Subset of the OIDC discovery document (RFC / OIDC Discovery 1.0) that we
 * rely on. `end_session_endpoint` and `scopes_supported` are both OPTIONAL
 * per spec — providers that omit them are tolerated (see `signOut` and
 * `resolveScopes`).
 */
export interface DiscoveryDocument {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
  scopes_supported?: string[];
  [key: string]: unknown;
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in: number;
  token_type: string;
}

const PKCE_VERIFIER_KEY = "tabgraph.pkce_verifier";
const OAUTH_STATE_KEY = "tabgraph.oauth_state";
const POST_LOGIN_REDIRECT_KEY = "tabgraph.post_login_redirect";
const REFRESH_TOKEN_KEY = "tabgraph.refresh_token";
const ID_TOKEN_KEY = "tabgraph.id_token";

// Requested scopes, in preference order. `offline_access` is required by
// several providers (Zitadel included) to issue a refresh token at all —
// but it's optional per the OIDC spec, so we drop it (see `resolveScopes`)
// when a provider's discovery document positively advertises it isn't
// supported.
const DEFAULT_SCOPES = ["openid", "profile", "email", "offline_access"];

export function config(): OidcConfig {
  const issuer = (
    import.meta.env.VITE_OIDC_ISSUER || "http://localhost:8082"
  ).replace(/\/+$/, "");
  const clientId = import.meta.env.VITE_OIDC_CLIENT_ID || "tabgraph-web";
  return { issuer, clientId };
}

// ---------------------------------------------------------------------------
// Discovery
// ---------------------------------------------------------------------------

const discoveryCache = new Map<string, Promise<DiscoveryDocument>>();

/**
 * Fetch (and cache, for the lifetime of the page) the issuer's discovery
 * document. Concurrent callers share the in-flight fetch.
 */
export async function discover(issuer: string): Promise<DiscoveryDocument> {
  const normalized = issuer.replace(/\/+$/, "");
  const cached = discoveryCache.get(normalized);
  if (cached) return cached;

  const promise = fetch(`${normalized}/.well-known/openid-configuration`)
    .then(async (res) => {
      if (!res.ok) {
        throw new Error(
          `OIDC discovery failed for ${normalized}: ${res.status}`,
        );
      }
      return (await res.json()) as DiscoveryDocument;
    })
    .catch((err: unknown) => {
      // Don't poison the cache with a failed lookup — a transient network
      // error shouldn't permanently break sign-in for the rest of the page
      // lifetime.
      discoveryCache.delete(normalized);
      throw err;
    });

  discoveryCache.set(normalized, promise);
  return promise;
}

/** Test-only: clear the discovery cache so each test starts from a clean slate. */
export function __resetDiscoveryCacheForTests(): void {
  discoveryCache.clear();
}

/**
 * Resolve the scope string to request, dropping any of our preferred scopes
 * that the provider's discovery document positively says it doesn't support.
 * If the provider omits `scopes_supported` entirely (allowed by spec), we
 * request the full default list — omission isn't a negative signal.
 */
function resolveScopes(doc: DiscoveryDocument): string {
  const supported = doc.scopes_supported;
  if (!Array.isArray(supported) || supported.length === 0) {
    return DEFAULT_SCOPES.join(" ");
  }
  const scopes = DEFAULT_SCOPES.filter((s) => supported.includes(s));
  if (!scopes.includes("openid")) scopes.unshift("openid");
  return scopes.join(" ");
}

// ---------------------------------------------------------------------------
// PKCE helpers
// ---------------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteLen = 32): string {
  const bytes = new Uint8Array(byteLen);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256(input: string): Promise<Uint8Array> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return new Uint8Array(digest);
}

// ---------------------------------------------------------------------------
// Authorization request
// ---------------------------------------------------------------------------

/**
 * Begin the sign-in flow by redirecting the browser to the provider's
 * authorization endpoint (from discovery). After successful sign-in, the
 * provider redirects to `redirectUri` with `?code=...&state=...` which is
 * handled by `handleCallback()`.
 *
 * `postLoginRedirect` is the path inside the SPA to land on after
 * successful sign-in — defaults to `/sessions`.
 */
export async function beginSignIn(
  redirectUri: string,
  postLoginRedirect = "/sessions",
): Promise<void> {
  const c = config();
  const doc = await discover(c.issuer);
  const verifier = randomString();
  const challenge = base64UrlEncode(await sha256(verifier));
  const state = randomString(16);

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(OAUTH_STATE_KEY, state);
  sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, postLoginRedirect);

  const params = new URLSearchParams({
    client_id: c.clientId,
    response_type: "code",
    redirect_uri: redirectUri,
    scope: resolveScopes(doc),
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  window.location.assign(`${doc.authorization_endpoint}?${params.toString()}`);
}

/**
 * Handle the OAuth callback. Verifies state, exchanges the authorization
 * code for tokens, stores them, returns the post-login redirect target.
 */
export async function handleCallback(
  redirectUri: string,
): Promise<{ redirectTo: string }> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const error = params.get("error");
  if (error) throw new Error(`OAuth error: ${error}`);
  if (!code) throw new Error("Missing authorization code");

  const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  if (!state || state !== expectedState) {
    throw new Error("OAuth state mismatch");
  }
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (!verifier) throw new Error("Missing PKCE verifier in sessionStorage");

  const c = config();
  const doc = await discover(c.issuer);
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: c.clientId,
    code,
    redirect_uri: redirectUri,
    code_verifier: verifier,
  });
  const res = await fetch(doc.token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`token exchange failed: ${res.status} ${text}`);
  }
  const tok = (await res.json()) as TokenResponse;

  setAccessToken(tok.access_token);
  // Refresh (and ID) tokens are stored in localStorage. This is intentionally
  // simple for MVP; a future iteration should move to an HttpOnly cookie via
  // a backend proxy endpoint.
  if (tok.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tok.refresh_token);
  }
  if (tok.id_token) {
    localStorage.setItem(ID_TOKEN_KEY, tok.id_token);
  }

  const redirectTo =
    sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY) || "/sessions";
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);

  return { redirectTo };
}

/**
 * Refresh the access token using the stored refresh token. Returns the new
 * access token, or null on failure.
 */
export async function refresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return null;

  const c = config();
  try {
    const doc = await discover(c.issuer);
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: c.clientId,
      refresh_token: refreshToken,
    });
    const res = await fetch(doc.token_endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!res.ok) {
      // Refresh token expired or revoked.
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(ID_TOKEN_KEY);
      return null;
    }
    const tok = (await res.json()) as TokenResponse;
    setAccessToken(tok.access_token);
    if (tok.refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, tok.refresh_token);
    }
    if (tok.id_token) {
      localStorage.setItem(ID_TOKEN_KEY, tok.id_token);
    }
    return tok.access_token;
  } catch {
    return null;
  }
}

/**
 * Sign out. Clears local tokens, then — if the provider advertises an
 * `end_session_endpoint` — redirects the browser there for RP-initiated
 * logout, with `post_logout_redirect_uri` pointing back at the SPA root.
 * Providers that omit `end_session_endpoint` (optional per spec) get a
 * purely local sign-out instead.
 */
export async function signOut(postLogoutRedirectUri?: string): Promise<void> {
  const c = config();
  const idToken = localStorage.getItem(ID_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ID_TOKEN_KEY);

  let doc: DiscoveryDocument | null = null;
  try {
    doc = await discover(c.issuer);
  } catch {
    // Discovery failed — fall back to local-only sign-out below.
  }

  if (doc?.end_session_endpoint) {
    const redirectUri = postLogoutRedirectUri ?? `${window.location.origin}/`;
    const params = new URLSearchParams({
      post_logout_redirect_uri: redirectUri,
      client_id: c.clientId,
    });
    if (idToken) params.set("id_token_hint", idToken);
    window.location.assign(`${doc.end_session_endpoint}?${params.toString()}`);
  }
  // else: no end_session_endpoint — local state is already cleared above.
}
